import { useGridDefinitions } from "@/controls/ag-grid/components/GridDefinitions";
import { ExcelButton } from "@/controls/buttons/ExcelButton";
import { CenteredLazyLoading } from "@/controls/LazyLoading";
import { routeInspectionsManager } from "@/setup/routes/inspection.routes";
import { buildUploadUrl } from "@/shared/models/Inspection";
import { ButtonWithLoading } from "@dexteel/mesf-core";
import KeyboardDoubleArrowDownIcon from "@mui/icons-material/KeyboardDoubleArrowDown";
import KeyboardDoubleArrowUpIcon from "@mui/icons-material/KeyboardDoubleArrowUp";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import {
	Box,
	Chip,
	FormControlLabel,
	Paper,
	Switch,
	Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { GridApi } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DrawingModal } from "../../components/DrawingModal";
import { InspectionDetailPanel } from "../../components/InspectionDetailPanel";
import {
	InspectionReportRow,
	InspectionReportSheetDetail,
} from "../../models/InspectionReport";
import { getInspectionReportPdf } from "../../repositories/InspectionReportRepository";
import { useTableData } from "../hooks/useTableData";
import { InspectionReportFilters } from "../InspectionsReportPage";
import {
	INSPECTION_EXCEL_STYLES,
	inspectionsExcelParams,
} from "../utils/excelExport";

type Props = {
	sheets: InspectionReportRow[];
	details: Map<number, InspectionReportSheetDetail> | undefined;
	filters: InspectionReportFilters;
	onError: (message: string) => void;
	isLoading: boolean;
	problemsOnly: boolean;
	onProblemsOnlyChange: (problemsOnly: boolean) => void;
	expandAll: boolean;
	onExpandAllChange: (expandAll: boolean) => void;
};

export const InspectionsReportTable = ({
	sheets,
	details,
	filters,
	onError,
	isLoading,
	problemsOnly,
	onProblemsOnlyChange,
	expandAll,
	onExpandAllChange,
}: Props) => {
	const navigate = useNavigate();
	const gridApi = useRef<GridApi<InspectionReportRow> | null>(null);
	const [drawing, setDrawing] = useState<InspectionReportRow | null>(null);
	const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

	const viewInManager = (sheet: InspectionReportRow) =>
		navigate(`${routeInspectionsManager}/${sheet.ShiftId}`);

	const { columnDefs } = useTableData({
		viewDrawing: setDrawing,
		viewInManager,
	});
	const { defaultColDef } = useGridDefinitions({});

	const expandedSheets = useRef(new Set<string>());
	const isApplyingExpansion = useRef(false);

	const applyExpansion = () => {
		isApplyingExpansion.current = true;

		gridApi.current?.forEachNode((node) => {
			if (node.id === undefined) return;

			if (expandAll) expandedSheets.current.add(node.id);

			node.setExpanded(expandedSheets.current.has(node.id), undefined, true);
		});

		isApplyingExpansion.current = false;
	};

	const syncExpandAll = () => {
		let sheetCount = 0;
		let expandedCount = 0;

		gridApi.current?.forEachNode((node) => {
			sheetCount += 1;
			if (node.expanded) expandedCount += 1;
		});

		if (sheetCount === 0) return;

		if (expandedCount === sheetCount && !expandAll) onExpandAllChange(true);
		else if (expandedCount === 0 && expandAll) onExpandAllChange(false);
	};

	useEffect(() => {
		if (!expandAll) expandedSheets.current.clear();

		applyExpansion();
	}, [expandAll]);

	const generatePdf = async () => {
		setIsGeneratingPdf(true);

		try {
			const blob = await getInspectionReportPdf({
				startDate: filters.startDate?.toDate().toISOString() ?? null,
				endDate: filters.endDate?.toDate().toISOString() ?? null,
				sectionId: filters.sectionId,
				eventId: filters.eventId,
				heatId: filters.heatId,
				surfaceDefectIds: filters.surfaceDefectIds,
				problemsOnly: filters.problemsOnly,
				sectionName: filters.sectionName,
				heatName: filters.heatName,
				surfaceDefectNames: filters.surfaceDefectNames,
			});

			const url = URL.createObjectURL(blob);
			window.open(url, "_blank", "noopener,noreferrer");
			setTimeout(() => URL.revokeObjectURL(url), 10_000);
		} catch (pdfError) {
			onError((pdfError as Error).message);
		} finally {
			setIsGeneratingPdf(false);
		}
	};

	return (
		<>
			<Paper
				elevation={2}
				sx={{
					height: "73vh",
					width: "100%",
					display: "flex",
					flexDirection: "column",
					"& .ag-details-row": { padding: 0 },
				}}
			>
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
						gap: 1,
						px: 1.5,
						py: 1,
						borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
					}}
				>
					<Typography variant="body2" color="text.secondary" fontWeight={600}>
						{sheets.length === 1 ? "1 sheet" : `${sheets.length} sheets`}
					</Typography>
					<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
						<FormControlLabel
							control={
								<Switch
									size="small"
									checked={problemsOnly}
									onChange={(event) =>
										onProblemsOnlyChange(event.target.checked)
									}
								/>
							}
							label="Problems only"
							slotProps={{
								typography: { fontSize: "0.8125rem", fontWeight: 500 },
							}}
							sx={{ mr: 2, whiteSpace: "nowrap" }}
						/>
						<Chip
							clickable
							aria-pressed={expandAll}
							icon={
								expandAll ? (
									<KeyboardDoubleArrowUpIcon sx={{ fontSize: 16 }} />
								) : (
									<KeyboardDoubleArrowDownIcon sx={{ fontSize: 16 }} />
								)
							}
							label={expandAll ? "Collapse all" : "Expand all"}
							onClick={() => onExpandAllChange(!expandAll)}
							sx={{
								backgroundColor: expandAll
									? (theme) => alpha(theme.palette.primary.main, 0.12)
									: "background.paper",
								border: 1,
								borderColor: expandAll
									? (theme) => alpha(theme.palette.primary.main, 0.5)
									: "divider",
								borderRadius: 1,
								color: expandAll ? "primary.main" : "text.secondary",
								fontSize: "0.8125rem",
								fontWeight: expandAll ? 600 : 500,
								height: 30,
								px: 0.5,
								transition:
									"background-color 120ms, border-color 120ms, color 120ms",
								"& .MuiChip-icon": { color: "inherit", ml: 0.75, mr: -0.25 },
								"&:hover": {
									backgroundColor: expandAll
										? (theme) => alpha(theme.palette.primary.main, 0.18)
										: "action.hover",
									borderColor: expandAll ? "primary.main" : "text.disabled",
									color: expandAll ? "primary.main" : "text.primary",
								},
							}}
						/>
						<ExcelButton
							size="small"
							fullWidth={false}
							disabled={isLoading || sheets.length === 0}
							handleExportToExcel={() => {
								const ordered: InspectionReportRow[] = [];

								gridApi.current?.forEachNodeAfterFilterAndSort((node) => {
									if (node.data) ordered.push(node.data);
								});

								gridApi.current?.exportDataAsExcel(
									inspectionsExcelParams(ordered, details),
								);
							}}
						/>
						<ButtonWithLoading
							variant="outlined"
							size="small"
							startIcon={
								<PictureAsPdfIcon sx={{ color: "error.main", fontSize: 18 }} />
							}
							isLoading={isGeneratingPdf}
							disabled={isLoading || sheets.length === 0}
							onClick={generatePdf}
							sx={{
								backgroundColor: "background.paper",
								borderColor: "divider",
								color: "text.secondary",
								fontSize: "0.8125rem",
								fontWeight: 500,
								textTransform: "none",
								whiteSpace: "nowrap",
								"&:hover": {
									backgroundColor: "action.hover",
									borderColor: "text.disabled",
								},
							}}
						>
							PDF
						</ButtonWithLoading>
					</Box>
				</Box>
				<Box
					sx={{
						flex: 1,
						minHeight: 0,
						"& .ag-header-cell": { fontSize: "0.5625rem" },
					}}
				>
					<AgGridReact<InspectionReportRow>
						loading={isLoading}
						rowData={sheets}
						columnDefs={columnDefs}
						defaultColDef={defaultColDef}
						excelStyles={INSPECTION_EXCEL_STYLES}
						rowHeight={34}
						headerHeight={30}
						animateRows
						masterDetail
						detailCellRenderer={InspectionDetailPanel}
						detailCellRendererParams={{ sheetDetails: details }}
						detailRowAutoHeight
						onGridReady={(event) => {
							gridApi.current = event.api;
							applyExpansion();
						}}
						onRowDataUpdated={applyExpansion}
						onRowGroupOpened={(event) => {
							if (event.node.id === undefined) return;

							if (event.node.expanded) {
								expandedSheets.current.add(event.node.id);

								if (!isApplyingExpansion.current) event.node.setSelected(true);
							} else {
								expandedSheets.current.delete(event.node.id);
							}

							if (!isApplyingExpansion.current) syncExpandAll();
						}}
						getRowId={(params) => `${params.data.InspectionId}`}
						loadingOverlayComponent={CenteredLazyLoading}
						gridOptions={{
							rowSelection: {
								mode: "singleRow",
								checkboxes: false,
								enableClickSelection: true,
							},
						}}
					/>
				</Box>
			</Paper>

			<DrawingModal
				open={drawing !== null}
				url={buildUploadUrl(drawing?.DrawingPath ?? null)}
				sectionName={drawing?.SectionName ?? ""}
				rev={drawing?.Rev ?? null}
				fileName={drawing?.DrawingFileName ?? null}
				onClose={() => setDrawing(null)}
			/>
		</>
	);
};
